// ---------------------------------------------------------------------------
// Ports legacy components/appointments/unconfirmed-call.js's `isSkipable` —
// decides whether Account Setup's Scheduling step should offer a "Skip"
// control for ONE named call (kickoff | thryv-success-services) on ONE
// location, instead of requiring a real booking before Continue unlocks.
//
// Legacy evaluated a flat {centers, apps} cart matched by product `name`;
// Account Setup's cart is a flat `items` array matched by `productCode` (see
// scheduling-call-rules.js's item-shape doc comment) with only an
// `alreadyIncluded` flag standing in for legacy's separate
// selectedCentersAndApps vs defaultCentersAndApps diff. Every legacy check
// below is re-expressed against that shape; nothing here changes the RULE,
// only what it reads.
//
// Deliberately NOT ported:
//   • enableSkipForLegacyWFCThryvPlan — legacy's v4 (non-catalog) cart +
//     selectedPlan concept has no equivalent in Account Setup, which is
//     always catalog/items-driven.
//   • The non-AEO-SEO half of legacy's isThryvLeadsOrSeoIncluded — that
//     legacy name-matching excluded every SEO product name that still exists
//     in the catalog, so schedulingLanguageRules.js already omits it for the
//     same reason (see its own comment) and this file inherits that gap.
// ---------------------------------------------------------------------------
import {hasStarterOnboardingOptIn} from '../../../../utils/scheduling-call-rules'
import {isThryvLeadsIncluded} from './schedulingLanguageRules'

// Ported verbatim from legacy unconfirmed-call.js — kept local rather than
// imported from that component so this stays a dependency-free rules file
// (same call schedulingLanguageRules.js and scheduling-call-rules.js already
// made for their own legacy ports).
const WFC_SKIP_ELIGIBLE_ROLES = new Set([
  'premisepilot',
  'premise',
  'directsales',
  'premisedemo',
  'reseller',
  'resellers',
])
const normalizeRole = role =>
  String(role || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '')

// legacy hasSeoInOrder matched product `name === 'aeo & seo'` across both
// centers and apps, with no "newly added" filter. Account Setup narrows it to
// NEWLY added AEO & SEO / Thryv Leads (see isNewThryvLeads / isNewAeoSeo), so
// an already-owned one no longer keeps the call mandatory for PartnerOps.
const AEO_SEO_CODE = 'seobase-5-thryv-m2m'

// legacy isNewLeadLift / isNewLeadLiftBNI (product-code half only — the
// v4/selectedAddons branch has no equivalent in Account Setup's cart).
const LEAD_LIFT_CODE = 'addon-5-leadslift-m2m'
const LEAD_LIFT_BNI_CODE = 'addon-5-leadslift-bni-m2m'

function isNewCenterWithPrefix(items, matches) {
  return items.some(
    item =>
      item.productType === 'CENTER' &&
      !item.alreadyIncluded &&
      matches(item.productCode ?? ''),
  )
}

// legacy isNewMC / isNewBC / isNewWFC / isNewKeap / isNewHomeServices —
// each compared selectedCentersAndApps.centers against
// defaultCentersAndApps.centers by code prefix; `!alreadyIncluded` is the
// Account Setup equivalent of "in selected but not in default".
const isNewMC = items =>
  isNewCenterWithPrefix(items, code => code.startsWith('mc_'))
const isNewBC = items =>
  isNewCenterWithPrefix(items, code => code.startsWith('bc'))
const isNewWFC = items =>
  isNewCenterWithPrefix(items, code => code.startsWith('wc_'))
const isNewKeap = items =>
  isNewCenterWithPrefix(items, code => code.startsWith('keap_'))
const isNewHomeServices = items =>
  isNewCenterWithPrefix(items, code => code.startsWith('max'))

const isNewAeoSeo = items =>
  items.some(item => !item.alreadyIncluded && item.productCode === AEO_SEO_CODE)

// isThryvLeadsIncluded is shared with the Spanish/Region rules and has no
// "new" notion, so narrow the cart to newly added items here instead of
// changing it.
const isNewThryvLeads = items =>
  isThryvLeadsIncluded(items.filter(item => !item.alreadyIncluded))

const isNewLeadLift = items =>
  items.some(
    item => !item.alreadyIncluded && item.productCode === LEAD_LIFT_CODE,
  )
const isNewLeadLiftBNI = items =>
  items.some(
    item => !item.alreadyIncluded && item.productCode === LEAD_LIFT_BNI_CODE,
  )

// Cart facts both role paths below read from.
function getCartFlags(items) {
  const newMC = isNewMC(items)
  const newBC = isNewBC(items)
  const newWFC = isNewWFC(items)
  const newKeap = isNewKeap(items)
  const newHomeServices = isNewHomeServices(items)
  return {
    newMC,
    newBC,
    newKeap,
    newHomeServices,
    standaloneNewWFC:
      newWFC && !newMC && !newBC && !newKeap && !newHomeServices,
    newThryvLeads: isNewThryvLeads(items),
    newAeoSeo: isNewAeoSeo(items),
  }
}

// PartnerOps: flag / role driven, same grants Core OOE honors. Deliberate
// departure from legacy unconfirmed-call.js, which skipped by cart contents
// alone (new MC / standalone WFC / Keap) and never read the features table.
// Nothing is skippable by default now — a new MC, new Keap plan or standalone
// WFC is skippable only when its grant is present.
//   • Every grant is subject to the restriction (SEO / Leads / new BC / Home
//     Services keep the call mandatory).
//   • Skip_Appointment (e.g. features row "Skip_Appointment / Role /
//     PartnerOps") only counts when the cart is also eligible — the legacy
//     cart rule: a new MC, new Keap plan or standalone new WFC.
// Never falls through to the TSS-alongside-kickoff or AU Lead Lift rules, even
// when this returns false.
function isCallSkippableForPartnerOps({cart, normalizedRoles, features}) {
  const restricted =
    cart.newThryvLeads || cart.newAeoSeo || cart.newBC || cart.newHomeServices
  if (restricted) return false

  const isCartEligible = cart.newMC || cart.standaloneNewWFC || cart.newKeap
  return Boolean(features.Skip_Appointment) && isCartEligible

  // const enableSkipForWFCEligibleRoles =
  //   normalizedRoles.some(role => WFC_SKIP_ELIGIBLE_ROLES.has(role)) &&
  //   cart.standaloneNewWFC
  // const enableSkipForMC =
  //   Boolean(features.Enable_Skip_Button_For_MC) && cart.newMC
  // const enableSkipForKeap =
  //   Boolean(features.Enable_Skip_Button_For_Keap) && cart.newKeap
  // return Boolean(features.Skip_Appointment) && (enableSkipForMC || enableSkipForKeap || enableSkipForWFCEligibleRoles)
}

// Core OOE (every non-PartnerOps role): driven by the features table and the
// WFC-eligible role list, plus the TSS and AU Lead Lift rules.
function isCallSkippableForCoreOoe({
  cart,
  items,
  country,
  callType,
  hasKickoffCall,
  normalizedRoles,
  features,
}) {
  // shouldRestrictPartnerOpsSkip is always false off the PartnerOps path, so
  // the legacy `&& !shouldRestrictPartnerOpsSkip` on this term is dropped.
  const enableSkipForMC =
    Boolean(features.Enable_Skip_Button_For_MC) && cart.newMC
  const enableSkipForKeap =
    Boolean(features.Enable_Skip_Button_For_Keap) && cart.newKeap
  const enableSkipForWFCEligibleRoles =
    normalizedRoles.some(role => WFC_SKIP_ELIGIBLE_ROLES.has(role)) &&
    cart.standaloneNewWFC

  if (
    features.Skip_Appointment ||
    enableSkipForMC ||
    enableSkipForKeap ||
    enableSkipForWFCEligibleRoles
  ) {
    return true
  }

  // legacy: the Thryv Success Services call is always skippable once a
  // kickoff call is also required for this location.
  if (callType === 'thryv-success-services' && hasKickoffCall) {
    return true
  }

  const newLeadLift =
    isNewLeadLift(items) ||
    (Boolean(features.Enable_BNI_Leads_Lift) && isNewLeadLiftBNI(items))
  return country === 'AU' && newLeadLift && !cart.newMC
}

/**
 * @param {object} params
 * @param {object} params.location - a PurchaseLinkSlice location ({items, country, ...})
 * @param {'kickoff'|'thryv-success-services'} params.callType
 * @param {boolean} params.hasKickoffCall - does this location ALSO require a
 *   kickoff call? (mirrors legacy's `apptData.displayKickOffCall`, read for
 *   the TSS-alongside-kickoff rule in the Core OOE path)
 * @param {Array<string>} [params.roles] - useUserRoles().roles
 * @param {object} [params.features] - useUserFeatures() result
 * @returns {boolean}
 */
export function isCallSkippable({
  location,
  callType,
  hasKickoffCall = false,
  roles = [],
  features = {},
}) {
  const items = location?.items ?? []

  // FULSAS-12295: Thryv Starter's onboarding opt-in kickoff call is mandatory
  // — never skippable, full stop, regardless of role or flags.
  if (hasStarterOnboardingOptIn(items)) return false

  const normalizedRoles = roles.map(normalizeRole)
  const cart = getCartFlags(items)

  if (normalizedRoles.includes('partnerops')) {
    return isCallSkippableForPartnerOps({cart, normalizedRoles, features})
  }

  return isCallSkippableForCoreOoe({
    cart,
    items,
    country: location?.country || 'US',
    callType,
    hasKickoffCall,
    normalizedRoles,
    features,
  })
}
